/**
 * Contract Editor Component
 * Szerződés létrehozása/szerkesztése
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import { 
  ContractFormData, 
  ContractService,
  CONTRACT_TYPE_OPTIONS,
  BILLING_FREQUENCY_OPTIONS,
  ContractType,
  BillingFrequency
} from '../../types/customerContract';
import { useProducts } from '../../hooks/useProducts';
import { createContractServiceFromProduct } from '../../services/customerContractService';
import {
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface ContractEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContractFormData) => Promise<void>;
  customerId: string;
  customerName: string;
  initialData?: Partial<ContractFormData>;
}

const ContractEditor: React.FC<ContractEditorProps> = ({
  isOpen,
  onClose,
  onSubmit,
  customerId,
  customerName,
  initialData
}) => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ContractFormData>({
    defaultValues: initialData || {
      customerId,
      contractType: 'fixed_term',
      billingFrequency: 'monthly',
      currency: 'HUF',
      autoRenew: false
    }
  });

  const { products } = useProducts();
  const [services, setServices] = useState<ContractService[]>(initialData?.services || []);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [customPrice, setCustomPrice] = useState<number | undefined>();
  const [priceNote, setPriceNote] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [billingType, setBillingType] = useState<'one_time' | 'recurring'>('recurring');
  const [includedHours, setIncludedHours] = useState<number>(0);
  const [overageRate, setOverageRate] = useState<number>(0);

  const watchedType = watch('contractType');

  // Szolgáltatás hozzáadása
  const handleAddService = () => {
    if (!selectedProductId) {
      toast.error('Válassz ki egy terméket/szolgáltatást!');
      return;
    }

    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;

    const hourPool = includedHours > 0 ? {
      includedHours,
      usedHours: 0,
      remainingHours: includedHours,
      overageHours: 0,
      resetPeriod: 'monthly' as const,
      overageRate
    } : undefined;

    const service = createContractServiceFromProduct(
      product,
      quantity,
      customPrice,
      priceNote,
      hourPool,
      billingType
    );

    setServices([...services, service]);
    
    // Reset form
    setSelectedProductId('');
    setCustomPrice(undefined);
    setPriceNote('');
    setQuantity(1);
    setBillingType('recurring');
    setIncludedHours(0);
    setOverageRate(0);
    
    toast.success('Szolgáltatás hozzáadva');
  };

  // Szolgáltatás törlése
  const handleRemoveService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
    toast.success('Szolgáltatás eltávolítva');
  };

  // Havi díj számítása (csak ismétlődő díjak!)
  const calculateMonthlyFee = () => {
    return services
      .filter(service => service.billingType === 'recurring')
      .reduce((sum, service) => sum + service.totalPrice, 0);
  };

  // Form submit
  const onFormSubmit = async (data: ContractFormData) => {
    if (services.length === 0) {
      toast.error('Adj hozzá legalább egy szolgáltatást!');
      return;
    }

    const formData: ContractFormData = {
      ...data,
      customerId,
      services,
      monthlyFee: calculateMonthlyFee()
    };

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting contract:', error);
    }
  };

  // Kiválasztott termék adatai
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const listPrice = selectedProduct?.netPrice || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Szerződés szerkesztése' : 'Új szerződés'}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Alapadatok */}
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-700 shadow-lg">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
              <DocumentTextIcon className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400">
              Szerződés alapadatok
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Ügyfél
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{customerName?.charAt(0) || '?'}</span>
                  </div>
                </div>
                <input
                  type="text"
                  value={customerName || 'Nincs kiválasztva'}
                  disabled
                  className="w-full pl-14 pr-3 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Szerződés neve *
              </label>
              <input
                type="text"
                {...register('contractName', { required: 'A szerződés neve kötelező' })}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 transition-all"
                placeholder="pl. Weboldal karbantartás 2024"
              />
              {errors.contractName && (
                <p className="text-red-500 text-sm mt-1">{errors.contractName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Szerződés típusa *
              </label>
              <select
                {...register('contractType', { required: true })}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 transition-all"
              >
                {CONTRACT_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Számlázási gyakoriság *
              </label>
              <select
                {...register('billingFrequency', { required: true })}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 transition-all"
              >
                {BILLING_FREQUENCY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Kezdő dátum *
              </label>
              <input
                type="date"
                {...register('startDate', { required: true })}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 transition-all"
              />
            </div>

            {watchedType === 'fixed_term' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Befejező dátum
                </label>
                <input
                  type="date"
                  {...register('endDate')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            )}
          </div>
        </div>

        {/* Szolgáltatások hozzáadása */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center mb-3">
            <TagIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-300">
              Szolgáltatások hozzáadása
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Termék/Szolgáltatás
              </label>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Válassz terméket/szolgáltatást/csomagot...</option>
                {products.filter(p => p.isActive).map(product => {
                  const typeIcon = product.type === 'package' ? '📦' : product.type === 'service' ? '⚙️' : '📦';
                  const typeLabel = product.type === 'package' ? 'Csomag' : product.type === 'service' ? 'Szolgáltatás' : 'Termék';
                  return (
                    <option key={product.id} value={product.id}>
                      {typeIcon} {product.code} - {product.name} ({typeLabel}, {product.netPrice?.toLocaleString()} {product.currency || 'HUF'})
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedProduct && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Listaár (nettó)
                  </label>
                  <input
                    type="number"
                    value={listPrice}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Szerződéses ár (nettó) *
                  </label>
                  <input
                    type="number"
                    value={customPrice !== undefined ? customPrice : listPrice}
                    onChange={(e) => setCustomPrice(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Egyedi ár (opcionális)"
                  />
                  {customPrice !== undefined && customPrice < listPrice && (
                    <p className="text-green-600 text-sm mt-1">
                      💰 Kedvezmény: {Math.round(((listPrice - customPrice) / listPrice) * 100)}% 
                      ({(listPrice - customPrice).toLocaleString()} Ft)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mennyiség
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Díjtípus
                  </label>
                  <select
                    value={billingType}
                    onChange={(e) => setBillingType(e.target.value as 'one_time' | 'recurring')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="recurring">🔄 Ismétlődő (havi díj része)</option>
                    <option value="one_time">1️⃣ Egyszeri díj</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {billingType === 'one_time' ? '⏱️ Benne foglalt órák (egyszeri)' : '⏱️ Foglalt órák (havi)'}
                  </label>
                  <input
                    type="number"
                    value={includedHours}
                    onChange={(e) => setIncludedHours(Number(e.target.value))}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder={billingType === 'one_time' ? 'pl. 10 óra setup' : 'pl. 5 óra/hó'}
                  />
                </div>

                {includedHours > 0 && billingType === 'recurring' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Túlóra díja (Ft/óra)
                    </label>
                    <input
                      type="number"
                      value={overageRate}
                      onChange={(e) => setOverageRate(Number(e.target.value))}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="pl. 20000"
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Megjegyzés az árról
                  </label>
                  <input
                    type="text"
                    value={priceNote}
                    onChange={(e) => setPriceNote(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="pl. Kedvezményes ár hosszú távú szerződés miatt"
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={handleAddService}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Szolgáltatás hozzáadása
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Hozzáadott szolgáltatások listája */}
          {services.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Hozzáadott szolgáltatások ({services.length})
              </h4>
              <div className="space-y-2">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {service.productName}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({service.productCode})
                        </span>
                        {/* Díjtípus badge */}
                        {service.billingType === 'one_time' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                            1️⃣ Egyszeri
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            🔄 Havi
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {service.listPrice !== service.contractPrice && (
                          <span className="line-through text-gray-400 mr-2">
                            {service.listPrice.toLocaleString()} Ft
                          </span>
                        )}
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {service.contractPrice.toLocaleString()} Ft
                        </span>
                        {service.discount && (
                          <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">
                            -{service.discount}%
                          </span>
                        )}
                        {service.quantity > 1 && (
                          <span className="ml-2">× {service.quantity}</span>
                        )}
                        {service.hourPool && (
                          <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded">
                            ⏱️ {service.hourPool.includedHours} óra/hó
                          </span>
                        )}
                      </div>
                      {service.priceNote && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                          💡 {service.priceNote}
                        </div>
                      )}
                      
                      {/* Csomag tartalom */}
                      {service.packageServices && service.packageServices.length > 0 && (
                        <div className="mt-2 bg-purple-50 dark:bg-purple-900/20 rounded-md p-2 border border-purple-200 dark:border-purple-700">
                          <div className="text-xs font-semibold text-purple-900 dark:text-purple-300 mb-1">
                            📦 Csomag tartalma ({service.packageServices.length} tétel)
                          </div>
                          <div className="space-y-1">
                            {service.packageServices.map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-xs">
                                <span className="text-purple-700 dark:text-purple-300">
                                  • {item.productName} ({item.productCode})
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {item.quantity} {item.unit || 'db'}
                                  {item.hours && ` (${item.hours} óra)`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveService(index)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-4"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Összesítés */}
              <div className="mt-4 space-y-2">
                {/* Havi díjak */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md border border-blue-200 dark:border-blue-700">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-900 dark:text-blue-300">
                      🔄 Havi díj összesen:
                    </span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      {calculateMonthlyFee().toLocaleString()} Ft + ÁFA
                    </span>
                  </div>
                </div>
                
                {/* Egyszeri díjak */}
                {services.some(s => s.billingType === 'one_time') && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-md border border-orange-200 dark:border-orange-700">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-orange-900 dark:text-orange-300">
                        1️⃣ Egyszeri díjak összesen:
                      </span>
                      <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {services
                          .filter(s => s.billingType === 'one_time')
                          .reduce((sum, s) => sum + s.totalPrice, 0)
                          .toLocaleString()} Ft + ÁFA
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Megjegyzések */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Megjegyzések
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            placeholder="További megjegyzések a szerződéshez..."
          />
        </div>

        {/* Gombok */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Mégse
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            {initialData ? 'Mentés' : 'Szerződés létrehozása'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ContractEditor;
